import * as Joi from 'joi';
import { commonV1DeprecatedProps } from './v1';
const numberFormatConfigSchema = Joi.object({
    __typename: Joi.string().valid('FormatConfig'),
    category: Joi.string().valid('NUMBER', 'PERCENTAGE', 'CURRENCY', 'CUSTOM'),
    numberFormatConfig: Joi.object({
        __typename: Joi.string().valid('NumberFormatConfig'),
        unit: Joi.string().valid('NONE', 'THOUSANDS', 'MILLION', 'BILLION', 'TRILLION', 'AUTO'),
        decimals: Joi.number(),
        negativeValueFormat: Joi.string().valid('PREFIX_DASH', 'SUFFIX_DASH', 'BRACES_NODASH'),
        toSeparateThousands: Joi.boolean(),
        removeTrailingZeroes: Joi.boolean().allow(null),
    }).allow(null),
    percentageFormatConfig: Joi.object({
        __typename: Joi.string().valid('PercentageFormatConfig'),
        decimals: Joi.number(),
        removeTrailingZeroes: Joi.boolean().allow(null),
    }).allow(null),
    currencyFormatConfig: Joi.object({
        __typename: Joi.string().valid('CurrencyFormatConfig'),
        locale: Joi.string(),
        unit: Joi.string().valid('NONE', 'THOUSANDS', 'MILLION', 'BILLION', 'TRILLION', 'AUTO'),
        decimals: Joi.number(),
        toSeparateThousands: Joi.boolean(),
        removeTrailingZeroes: Joi.boolean().allow(null),
    }).allow(null),
    customFormatConfig: Joi.object({
        __typename: Joi.string().allow('CustomFormatConfig'),
        format: Joi.string().allow(null),
    }).allow(null),
    isCategoryEditable: Joi.boolean().allow(null),
});
const AxisPropertiesSchema = Joi.object({
    __typename: Joi.string().valid('AxisProperties'),
    format: numberFormatConfigSchema.allow(null),
    name: Joi.string().allow(null),
    yAxisRange: Joi.object({
        __typename: Joi.string().valid('RangeParam'),
        min: Joi.number()
            .unsafe()
            .allow(null),
        max: Joi.number()
            .unsafe()
            .allow(null),
    }).allow(null),
    isOpposite: Joi.boolean().allow(null),
    axisType: Joi.string().valid('X', 'Y'),
    linkedColumns: Joi.array().items(Joi.string()),
    customProps: Joi.string().allow(null),
});
const ColumnPropertiesSchemaV2 = Joi.object({
    __typename: Joi.string(),
    dataLabels: Joi.boolean().allow(null),
    format: numberFormatConfigSchema.allow(null),
});
var CHART_RESPONSIVE_LAYOUT_V2;
(function (CHART_RESPONSIVE_LAYOUT_V2) {
    CHART_RESPONSIVE_LAYOUT_V2["USER_PREFERRED_ON"] = "USER_PREFERRED_ON";
    CHART_RESPONSIVE_LAYOUT_V2["USER_PREFERRED_OFF"] = "USER_PREFERRED_OFF";
    CHART_RESPONSIVE_LAYOUT_V2["AUTO_ON"] = "AUTO_ON";
    CHART_RESPONSIVE_LAYOUT_V2["AUTO_OFF"] = "AUTO_OFF";
})(CHART_RESPONSIVE_LAYOUT_V2 || (CHART_RESPONSIVE_LAYOUT_V2 = {}));
const ChartPropertiesSchemaV2 = Joi.object({
    __typename: Joi.string(),
    visibleSeriesNames: Joi.array()
        .items(Joi.string().allow(''))
        .allow(null),
    axisExtremes: Joi.object({
        x: Joi.array().items(Joi.object({
            min: Joi.number().allow(null),
            max: Joi.number().allow(null),
        })),
        y: Joi.array().items(Joi.object({
            min: Joi.number().allow(null),
            max: Joi.number().allow(null),
        })),
    }).allow(null),
    gridLines: Joi.object({
        xGridlineEnabled: Joi.boolean().allow(null),
        yGridlineEnabled: Joi.boolean().allow(null),
    }).allow(null),
    isZoomed: Joi.boolean().allow(null),
    mapviewport: Joi.object({
        center: Joi.array()
            .length(2)
            .items(Joi.number()),
        zoomLevel: Joi.number(),
    }).allow(null),
    responsiveLayoutPreference: Joi.string()
        .valid(CHART_RESPONSIVE_LAYOUT_V2.AUTO_OFF, CHART_RESPONSIVE_LAYOUT_V2.AUTO_ON, CHART_RESPONSIVE_LAYOUT_V2.USER_PREFERRED_OFF, CHART_RESPONSIVE_LAYOUT_V2.USER_PREFERRED_ON, 1, 2, 3, 4)
        .allow(null),
    responsiveLayoutDisabled: Joi.boolean().allow(null),
    showLinearRegressionLine: Joi.boolean().allow(null),
    chartSpecific: Joi.object({
        markersEnabled: Joi.boolean().allow(null),
        isStackedAsPercent: Joi.boolean().allow(null),
        stackedAsPercentFormat: numberFormatConfigSchema.allow(null),
        pivotState: Joi.object({
            rowExpandedPaths: Joi.array().items(Joi.array().items(Joi.string())),
            columnExpandedPaths: Joi.array().items(Joi.array().items(Joi.string())),
            useTreeLayout: Joi.boolean(),
            showPivotSummaries: Joi.boolean(),
        }).allow(null),
        hidePivotSummaries: Joi.boolean().allow(null),
        summaryMode: Joi.string().allow(null),
        useFlatLayout: Joi.boolean().allow(null),
        isHeatmapOverlayed: Joi.boolean().allow(null),
        summaryFormat: numberFormatConfigSchema.allow(null),
    })
        .pattern(/\d/, Joi.any()),
    showStackedLabels: Joi.boolean().allow(null),
    showRoundedNumbers: Joi.boolean().allow(null),
    allLabels: Joi.boolean().allow(null),
    dataSize: Joi.number().allow(null),
});
class ClientStateV2Class {
}
const ClientStateV2Schema = Joi.object({
    axisProperties: Joi.object().pattern(Joi.string(), AxisPropertiesSchema),
    columnProperties: Joi.object().pattern(Joi.string(), ColumnPropertiesSchemaV2),
    chartProperties: ChartPropertiesSchemaV2,
    custom_color_selector_array: Joi.array().items(Joi.string()),
    systemSeriesColors: Joi.object().pattern(Joi.string().allow(''), Joi.string()),
    seriesColors: Joi.object().pattern(Joi.string().allow(''), Joi.string()),
    systemMultiColorSeriesColors: Joi.object().pattern(Joi.string(), Joi.object().pattern(Joi.string().allow(''), Joi.array().items(Joi.string().allow(null)))),
    multiColorSeriesColors: Joi.object().pattern(Joi.string(), Joi.object().pattern(Joi.string().allow(''), Joi.array().items(Joi.string().allow(null)))),
    version: Joi.string().valid('V2', 'V2.1', 'V2.2'),
    ...commonV1DeprecatedProps,
});
export { ClientStateV2Class, CHART_RESPONSIVE_LAYOUT_V2, ClientStateV2Schema, ChartPropertiesSchemaV2, AxisPropertiesSchema, numberFormatConfigSchema, };
//# sourceMappingURL=v2.js.map